import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseQuery } from './getBaseQuery';
import { BatchExportLabelRequest, BatchExportLabelResponse, DeleteWithLinksRequest, DeleteWithLinksResponse, ExportLabelRequest, ExportLabelResponse, ExportWellResultsReportRequest, ExportWellResultsReportResponse, GetInterpolationMapsRequest, GetInterpolationMapsResponse, GetReportWellInfoRequest, GetReportWellInfoResponse, GetWellByProjectRequest, GetWellByProjectResponse, GetWellCategoryRequest, GetWellCategoryResponse, GetWellLabelInfoRequest, GetWellLabelInfoResponse, GetWellRequest, GetWellResponse, GetWellUuidRequest, GetWellUuidResponse, PatchWellRequest, PatchWellResponse, PostBulkWellRequest, PostBulkWellResponse, PostCreateWithLinksRequest, PostCreateWithLinksResponse, PostWellExportRequest, PostWellRequest, PostWellResponse, WellGetCommentsRequest, WellGetCommentsResponse } from './types/TWellServices';

const URL_Prefix = '/well';

export const wellService = createApi({
    reducerPath: 'wellService',
    baseQuery: getBaseQuery,
    tagTypes: ['Well'],
    endpoints: (builder) => ({
        getWell: builder.query<GetWellResponse, GetWellRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/`,
                method: 'GET',
                params: params,
            }),
        }),
        getWellUuid: builder.query<GetWellUuidResponse, GetWellUuidRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/${params.uuid}/`,
                method: 'GET',
            }),
        }),
        postWell: builder.mutation<PostWellResponse, PostWellRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Well'],
        }),
        postBulkWell: builder.mutation<PostBulkWellResponse, PostBulkWellRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/bulk_create_with_links/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Well'],
        }),
        getWellByProject: builder.query<GetWellByProjectResponse, GetWellByProjectRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/get_by_project/`,
                method: 'GET',
                params: params,
            }),
        }),
        patchWell: builder.mutation<PatchWellResponse, PatchWellRequest>({
            query: ({ uuid, ...body }) => ({
                url: `${URL_Prefix}/${uuid}/`,
                method: 'PATCH',
                body: body,
            }),
            invalidatesTags: ['Well'],
        }),
        exportWellResultsReport: builder.mutation<Blob, ExportWellResultsReportRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/export_well_results_report/`,
                method: 'POST',
                body: body,
                responseHandler: async (response) => {
                    const blob = await response.blob();

                    // Создаем ссылку для скачивания
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = url;

                    // Получаем имя файла из заголовков или используем дефолтное
                    const contentDisposition = response.headers.get('content-disposition');
                    let filename = `${body.name}.docx`;

                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                        if (filenameMatch && filenameMatch[1]) {
                            filename = filenameMatch[1];
                        }
                    }
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);

                    return blob;
                },
                cache: 'no-cache',
            }),
            invalidatesTags: ['Well'],
        }),
        postCreateWithLinks: builder.mutation<PostCreateWithLinksResponse, PostCreateWithLinksRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/create_with_links/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Well'],
        }),
        getWellCategorySelector: builder.query<GetWellCategoryResponse, GetWellCategoryRequest>({
            query: (params) => ({
                url: `/well_category/selector/`,
                method: 'GET',
                params: params,
            }),
        }),
        deleteWithLinks: builder.mutation<DeleteWithLinksResponse, DeleteWithLinksRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/${body.uuid}/delete_with_links/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Well'],
        }),
        getWellLabelInfo: builder.query<GetWellLabelInfoResponse, GetWellLabelInfoRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/label_info/${params.well_uuid}/`,
                method: 'GET',
            }),
        }),
        postExportLabel: builder.mutation<ExportLabelResponse, ExportLabelRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/export_label/${body.well_uuid}/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Well'],
        }),
        batchExportLabel: builder.mutation<BatchExportLabelResponse, BatchExportLabelRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/batch_export_labels/${body.project_uuid}/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Well'],
        }),
        getWellInterpolationMaps: builder.query<GetInterpolationMapsResponse, GetInterpolationMapsRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/${params.uuid}/get_interpolation_maps/`,
                method: 'GET',
            }),
        }),
        wellImport: builder.mutation<void, { file: File; area_uuid: string }>({
            query: (body) => {
                const formData = new FormData();
                formData.append('file', body.file);
                formData.append('area_uuid', body.area_uuid);

                return {
                    url: `${URL_Prefix}/import/`,
                    method: 'POST',
                    body: formData,
                    // Явно убираем автоматический Content-Type
                    headers: {
                        // Пустой объект заголовков заставит браузер установить правильный Content-Type
                    },
                };
            },
            invalidatesTags: ['Well'],
        }),
        getWellComments: builder.query<WellGetCommentsResponse, WellGetCommentsRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/get_comments/${params.well_uuid}`,
                method: 'GET',
            }),
        }),
        postWellExport: builder.mutation<Blob, PostWellExportRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/export/`,
                method: 'POST',
                body: body,
                responseHandler: async (response) => {
                    const blob = await response.blob();

                    // Создаем ссылку для скачивания
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = url;

                    //Получаем имя файла из заголовков или используем дефолтное
                    const contentDisposition = response.headers.get('content-disposition');
                    let filename = `Сводный_отчет_по_скважинам_${body?.date}.docx`;

                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                        if (filenameMatch && filenameMatch[1]) {
                            filename = filenameMatch[1];
                        }
                    }
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);

                    return blob;
                },
                cache: 'no-cache',
            }),
            invalidatesTags: ['Well'],
        }),
        getReportWellInfo: builder.query<GetReportWellInfoResponse, GetReportWellInfoRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/${params.uuid}/get_report_name_info/`,
                method: 'GET',
            }),
        }),
        wellDensityImport: builder.mutation<void, { file: File; area_uuid: string }>({
            query: (body) => {
                const formData = new FormData();
                formData.append('file', body.file);
                formData.append('area_uuid', body.area_uuid);

                return {
                    url: `${URL_Prefix}/import_well_density/`,
                    method: 'POST',
                    body: formData,
                    // Явно убираем автоматический Content-Type
                    headers: {
                        // Пустой объект заголовков заставит браузер установить правильный Content-Type
                    },
                };
            },
            invalidatesTags: ['Well'],
        }),
    }),
});

export const {
    useGetWellByProjectQuery,
    useGetWellQuery,
    useLazyGetWellByProjectQuery,
    useLazyGetWellQuery,
    usePostWellMutation,
    useGetWellUuidQuery,
    useLazyGetWellUuidQuery,
    usePatchWellMutation,
    useExportWellResultsReportMutation,
    usePostCreateWithLinksMutation,
    useGetWellCategorySelectorQuery,
    useLazyGetWellCategorySelectorQuery,
    useDeleteWithLinksMutation,
    useGetWellLabelInfoQuery,
    useLazyGetWellLabelInfoQuery,
    useBatchExportLabelMutation,
    usePostExportLabelMutation,
    useGetWellInterpolationMapsQuery,
    useLazyGetWellInterpolationMapsQuery,
    useWellImportMutation,
    useGetWellCommentsQuery,
    useLazyGetWellCommentsQuery,
    usePostWellExportMutation,
    useGetReportWellInfoQuery,
    useLazyGetReportWellInfoQuery,
    useWellDensityImportMutation,
    usePostBulkWellMutation
} = wellService;